/**
 * FocusVisual — Phase 4 / V1 (Feb 28, 2026).
 *
 * Tiny SVG visual primitive that supports the Homework Continuity
 * Bridge by rendering a *minimal* top-down diagram of the learner's
 * current "This time" focus — so the learner sees what we mean.
 *
 * Operator directive (Phase 4):
 *   • Mounts inside HomeworkContinuityBridge under the continuation
 *     anchor — NOT as a separate dashboard card.
 *   • No new backend endpoint, no schema, no AI generation, no
 *     animation, no separate visual-learning page.
 *   • Silent fallback: when the focus doesn't match a known visual
 *     type → renders null.
 *
 * Doctrine pins baked in:
 *   • Pure render. No hooks, no fetch, no animation, no transition.
 *   • Curated registry — focus title → svg key — is a tiny pure
 *     function. Exported for doctrine testing.
 *   • Six initial types (roundabout / junction / mirrors / meeting /
 *     lane / speed). No others.
 *   • Each SVG is static, minimal, single-colour, ≤200 px wide.
 *   • No gamification language anywhere.
 *   • No score / risk / rating language anywhere.
 *
 * The visual supports understanding, not assessment.
 */

/**
 * resolveFocusVisualKey — pure function. Given a free-text focus
 * title (e.g. "Roundabouts", "Mirrors & moving off"), return one of
 * the six known visual keys, or null if no safe match.
 *
 * Exported for doctrine pinning.
 */
export function resolveFocusVisualKey(focus) {
  const t = (focus || "").toString().trim().toLowerCase();
  if (!t) return null;
  if (t.includes("roundabout"))                                 return "roundabout";
  if (t.includes("junction") || t.includes("crossroads"))       return "junction";
  if (t.includes("mirror") || t.includes("msm") || t.includes("moving off")) return "mirrors";
  if (t.includes("meeting traffic") || t.includes("oncoming"))  return "meeting";
  if (t.includes("lane") || t.includes("position"))             return "lane";
  if (t.includes("speed") || t.includes("limit"))               return "speed";
  return null;
}

/**
 * KNOWN_FOCUS_VISUAL_KEYS — exported so doctrine pins can enumerate
 * the supported initial set without inferring from regex.
 */
export const KNOWN_FOCUS_VISUAL_KEYS = Object.freeze([
  "roundabout",
  "junction",
  "mirrors",
  "meeting",
  "lane",
  "speed",
]);

// ── SVG primitives ──────────────────────────────────────────────
// Each primitive is a static minimal top-down diagram. No animation,
// no gradient theatre. Single accent colour (currentColor) for the
// learner's car so the parent can theme by container.

const SVG_VIEWBOX = "0 0 200 80";
const SVG_COMMON_ATTRS = {
  viewBox: SVG_VIEWBOX,
  width: "100%",
  height: "auto",
  role: "img",
  preserveAspectRatio: "xMidYMid meet",
  className: "max-w-[200px] mt-2 text-emerald-600 dark:text-emerald-400",
};

function Roundabout() {
  return (
    <svg {...SVG_COMMON_ATTRS} aria-label="Roundabout diagram">
      {/* approach road */}
      <rect x="0" y="34" width="60" height="12" fill="#e2e8f0" />
      {/* exit road */}
      <rect x="140" y="34" width="60" height="12" fill="#e2e8f0" />
      {/* roundabout island */}
      <circle cx="100" cy="40" r="22" fill="none" stroke="#94a3b8" strokeWidth="2" />
      <circle cx="100" cy="40" r="10" fill="#cbd5e1" />
      {/* learner's car (currentColor accent) */}
      <rect x="40" y="36" width="14" height="8" rx="2" fill="currentColor" />
      {/* directional arrow */}
      <path d="M 130 40 l -6 -4 v 2.5 H 110 v 3 H 124 v 2.5 z" fill="#64748b" />
    </svg>
  );
}

function Junction() {
  return (
    <svg {...SVG_COMMON_ATTRS} aria-label="Junction diagram">
      <rect x="0" y="34" width="200" height="12" fill="#e2e8f0" />
      <rect x="94" y="0" width="12" height="80" fill="#e2e8f0" />
      <rect x="40" y="36" width="14" height="8" rx="2" fill="currentColor" />
      <path d="M 130 40 l -6 -4 v 2.5 H 110 v 3 H 124 v 2.5 z" fill="#64748b" />
    </svg>
  );
}

function Mirrors() {
  return (
    <svg {...SVG_COMMON_ATTRS} aria-label="Mirrors and moving off diagram">
      <rect x="0" y="34" width="200" height="12" fill="#e2e8f0" />
      <rect x="60" y="36" width="14" height="8" rx="2" fill="currentColor" />
      {/* mirror-glance arcs above the car */}
      <path d="M 55 30 Q 67 18 79 30" stroke="#64748b" strokeWidth="1.5" fill="none" />
      <path d="M 64 28 Q 67 22 70 28" stroke="#64748b" strokeWidth="1.5" fill="none" />
      <path d="M 130 40 l -6 -4 v 2.5 H 110 v 3 H 124 v 2.5 z" fill="#64748b" />
    </svg>
  );
}

function Meeting() {
  return (
    <svg {...SVG_COMMON_ATTRS} aria-label="Meeting oncoming traffic diagram">
      <rect x="0" y="34" width="200" height="12" fill="#e2e8f0" />
      {/* parked obstruction */}
      <rect x="120" y="26" width="20" height="8" rx="2" fill="#94a3b8" />
      {/* learner */}
      <rect x="20" y="36" width="14" height="8" rx="2" fill="currentColor" />
      {/* oncoming */}
      <rect x="170" y="36" width="14" height="8" rx="2" fill="#475569" />
    </svg>
  );
}

function Lane() {
  return (
    <svg {...SVG_COMMON_ATTRS} aria-label="Lane positioning diagram">
      <rect x="0" y="22" width="200" height="36" fill="#e2e8f0" />
      <line x1="0" y1="40" x2="200" y2="40" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="6 6" />
      <rect x="80" y="42" width="14" height="8" rx="2" fill="currentColor" />
    </svg>
  );
}

function Speed() {
  return (
    <svg {...SVG_COMMON_ATTRS} aria-label="Speed control diagram">
      <rect x="0" y="34" width="200" height="12" fill="#e2e8f0" />
      <rect x="40" y="36" width="14" height="8" rx="2" fill="currentColor" />
      {/* speed sign — round, no number to stay generic */}
      <circle cx="120" cy="22" r="10" fill="#fee2e2" stroke="#ef4444" strokeWidth="2" />
    </svg>
  );
}

const REGISTRY = Object.freeze({
  roundabout: Roundabout,
  junction:   Junction,
  mirrors:    Mirrors,
  meeting:    Meeting,
  lane:       Lane,
  speed:      Speed,
});

/**
 * FocusVisual — default export. Takes a free-text focus title and
 * renders the matching SVG, or null when no safe match exists.
 *
 * Props:
 *   focus    — string. Free-text focus title (e.g. "Roundabouts").
 *   testId   — optional custom test id; defaults to "focus-visual".
 */
export default function FocusVisual({ focus, testId = "focus-visual" }) {
  const key = resolveFocusVisualKey(focus);
  if (!key) return null;
  const Diagram = REGISTRY[key];
  if (!Diagram) return null;
  return (
    <div
      data-testid={testId}
      data-focus-visual-key={key}
      className="flex justify-start"
    >
      <Diagram />
    </div>
  );
}
